import { SeatPriceMap as SeatPriceMapType } from '@/src/types/itinerary'
import { TranslationFunction } from '@/src/types/translations'
import { Box, Typography } from '@mui/material'

const SeatPriceMap = ({
    seatPriceMap,
    formatCurrency,
    t
}: {
    seatPriceMap: SeatPriceMapType[]
    formatCurrency: (price: number) => string
    t: TranslationFunction<'itineraryPage.addons'>
}) => {
    return (
        <Box
            sx={{
                width: { lg: '200px' },
                minWidth: '150px',
                bgcolor: 'white',
                m: { xs: 0, lg: 2 },
                p: { xs: 1.5, lg: 2 },
                borderRadius: { xs: 0, lg: 1 },
                overflowX: 'scroll',
                boxShadow: 'none',
                height: 'fit-content',
                display: 'flex',
                flexWrap: 'nowrap',
                flexDirection: { xs: 'row', lg: 'column' },
                gap: { xs: 1.5, lg: 2 },
                justifyContent: 'flex-start',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                    display: 'none'
                }
            }}
        >
            {seatPriceMap.map((priceMap, index) => {
                const { start, end, seatColor } = priceMap
                let displayPrice = ''
                if (start === end) {
                    displayPrice = start === 0 ? t('Free') : formatCurrency(start)
                } else {
                    displayPrice = `${formatCurrency(start)} - ${formatCurrency(end)}`
                }
                return (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.75, lg: 1 },
                            flexDirection: { xs: 'column', sm: 'row' },
                            textAlign: { xs: 'center', sm: 'left' },
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 16, lg: 20 },
                                height: { xs: 16, lg: 20 },
                                bgcolor: seatColor,
                                borderRadius: '4px',
                                flexShrink: 0
                            }}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 500,
                                fontSize: { xs: '11px', lg: '12px' },
                                lineHeight: 1.2
                            }}
                        >
                            {displayPrice}
                        </Typography>
                    </Box>
                )
            })}
            {/* Extra Legroom */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.75, lg: 1 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    textAlign: { xs: 'center', sm: 'left' }
                }}
            >
                <Box
                    sx={{
                        width: { xs: 16, lg: 20 },
                        height: { xs: 16, lg: 20 },
                        border: '1px solid',
                        borderColor: 'borders.default',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'textColors.primary',
                            fontSize: { xs: 6, lg: 8 },
                            fontWeight: 'bold'
                        }}
                    >
                        XL
                    </Typography>
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                        fontSize: { xs: '11px', lg: '12px' },
                        lineHeight: 1.2
                    }}
                >
                    {t('extraLegroom')}
                </Typography>
            </Box>

            {/* Unavailable */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.75, lg: 1 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    textAlign: { xs: 'center', sm: 'left' }
                }}
            >
                <Box
                    sx={{
                        width: { xs: 16, lg: 20 },
                        height: { xs: 16, lg: 20 },
                        bgcolor: 'seatColors.unavailable',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'white',
                            fontSize: { xs: 8, lg: 10 }
                        }}
                    >
                        ✕
                    </Typography>
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 500,
                        fontSize: { xs: '11px', lg: '12px' },
                        lineHeight: 1.2
                    }}
                >
                    {t('Booked')}
                </Typography>
            </Box>
        </Box>
    )
}

export default SeatPriceMap
